import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import { AlertSeverityEnum, Mantenimiento, MantenimientoPagingRes, UseMutationsParams } from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchMantenimiento = (params?: GetMantenimientoParams) => {
  return useQuery({
    queryKey: ['adminMantenimiento', ...Object.values(params || {})],
    queryFn: async () => {
      return getMantenimientos(params);
    },
  });
};

export const useGetMantenimiento = (id: string) => {
  return useQuery({
    queryKey: ['adminMantenimiento', id],
    queryFn: async () => {
      return getMantenimiento(id);
    },
  });
};

export const useCreateMantenimiento = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createMantenimiento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMantenimiento'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Mantenimiento creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateMantenimiento = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateMantenimiento,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminMantenimiento'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Mantenimiento actualizada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetMantenimientoParams = {
  page?: number;
  page_size?: number;
  nombre_activo?: string;
  codigo?: string;
  fecha_inicio_after?: string;
  fecha_fin_before?: string;
  activo?: string;
};
export type CreateMantenimientoParams = Omit<
  Mantenimiento,
  'id_mantenimiento'
>;
export type UpdateMantenimientoParams = {
  id: string;
  data: CreateMantenimientoParams;
};

export const getMantenimientos = async (params?: GetMantenimientoParams) => {
  const queryParams = getUrlParamsFetch({ ...params });

  return get<MantenimientoPagingRes>(
    `${urlBackend}api/v1/mantenimientoactivo/?${queryParams}`,
    true
  );
};

export const getMantenimiento = async (id: string) => {
  try {
    return await get<Mantenimiento>(
      `${urlBackend}api/v1/mantenimientoactivo/${id}`,
      true
    );
  }
  catch (error) {
    return handleAxiosError(error);
  }
};

export const createMantenimiento = async (data: CreateMantenimientoParams) => {
  const setAlert = useUiStore.getState().setAlert;
  const res = await post<Mantenimiento>(
    `${urlBackend}api/v1/mantenimientoactivo/`,
    data,
    true
  );

  setAlert(true, res.message, res.status);
  return res;
};

export const updateMantenimiento = async ({
  id,
  data,
}: UpdateMantenimientoParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<Mantenimiento>(`${urlBackend}api/v1/mantenimientoactivo/${id}/`, data, true);
};
